// src/components/events/EventsCarouselOne.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import EventCard from './EventCard';

export default function EventsCarouselOne({
  events = [],
  slug,
  color,
  autoPlay = false,
  autoPlayMs = 4000,
}) {
  const trackRef = useRef(null);
  const itemRefs = useRef([]);
  const [index, setIndex] = useState(0);

  // asegurar refs por longitud
  itemRefs.current = useMemo(
    () => Array(events.length).fill(null).map((_, i) => itemRefs.current[i] || React.createRef()),
    [events.length]
  );

  const clamp = (n) => Math.max(0, Math.min(events.length - 1, n));

  const goTo = (i) => {
    const idx = clamp(i);
    setIndex(idx);

    const parent = trackRef.current;
    const child = itemRefs.current[idx]?.current;

    if (parent && child) {
      // Calculamos la posición centrada sin afectar el scroll global de la ventana
      const targetLeft = child.offsetLeft - (parent.clientWidth - child.clientWidth) / 2;
      parent.scrollTo({
        left: targetLeft,
        behavior: 'smooth'
      });
    }
  };

  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  // Seguir el scroll manual (drag/teclado) para mover el dot/estado
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        // elegimos el slide cuyo centro esté más cerca del centro del viewport del carrusel
        const center = el.scrollLeft + el.clientWidth / 2;
        let best = 0, bestDist = Infinity;
        itemRefs.current.forEach((r, i) => {
          const node = r.current;
          if (!node) return;
          const rectLeft = node.offsetLeft + node.clientWidth / 2;
          const dist = Math.abs(rectLeft - center);
          if (dist < bestDist) { bestDist = dist; best = i; }
        });
        setIndex(best);
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [events.length]);

  // Auto-play de a 1
  useEffect(() => {
    if (!autoPlay || events.length <= 1) return;
    const id = setInterval(() => {
      if (index >= events.length - 1) goTo(0);
      else next();
    }, autoPlayMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, autoPlayMs, index, events.length]);

  if (!events.length) return null;

  return (
    <div className="relative">
      {/* Flechas */}
      <button
        onClick={prev}
        disabled={index === 0}
        aria-label="Anterior"
        className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 rounded-full bg-surface/80 backdrop-blur border border-white/10 shadow-xl px-4 py-3 text-white transition-all
        ${index === 0 ? 'opacity-0 scale-90 pointer-events-none' : 'hover:bg-surface opacity-100 scale-100'}`}
      >
        ‹
      </button>

      <button
        onClick={next}
        disabled={index === events.length - 1}
        aria-label="Siguiente"
        className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 rounded-full bg-surface/80 backdrop-blur border border-white/10 shadow-xl px-4 py-3 text-white transition-all
        ${index === events.length - 1 ? 'opacity-0 scale-90 pointer-events-none' : 'hover:bg-surface opacity-100 scale-100'}`}
      >
        ›
      </button>

      {/* Track */}
      <div
        ref={trackRef}
        className="
          overflow-x-auto no-scrollbar scroll-smooth
          snap-x snap-mandatory
          px-6 -mx-6 pt-2 pb-6
        "
      >
        <div className="flex gap-6">
          {events.map((ev, i) => (
            <div
              key={ev._id}
              ref={itemRefs.current[i]}
              // 1 por vista en mobile (centrado), 2 en sm, 3 en lg; el avance sigue siendo 1
              className="
                snap-center shrink-0
                w-[86%] sm:w-[60%] lg:w-[32%]
              "
            >
              <EventCard ev={ev} slug={slug} color={color} />
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      <div className="mt-2 flex justify-center gap-2">
        {events.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Ir al slide ${i + 1}`}
            className={`h-1.5 transition-all rounded-full ${i === index ? 'w-6 bg-primary-orange' : 'w-1.5 bg-white/20'}`}
          />
        ))}
      </div>
    </div>
  );
}